import { useEffect, useState } from "react";
import { BsYoutube } from "react-icons/bs";
import { UppyFile } from "@uppy/core";
import { useSession } from "next-auth/react";
import {
  FormProvider,
  Controller,
  useForm,
  useFormContext,
  FieldError,
} from "react-hook-form";
import BrandSelect from "../components/BrandSelect";
import FileUploadUppy from "./FileUploadUppy";

import {
  TRANSMISSIONS,
  DRIVETRAINS,
  FUELS,
  CONDITIONS,
  MILEAGE_UNIT_KEYS,
  CURRENCY_KEYS,
  CURRENCIES,
  MIN_ALLOWED_UPLOADS,
  MAX_ALLOWED_UPLOADS,
  CATEGORIES,
  PROVINCES,
  FUEL_KEYS,
  YES_NO,
  SUBSCRIPTION_TYPES,
} from "../utils/constants";
import ComboInfoPanel from "../components/ComboInfoPanel";
import { inputClass } from "../utils/classes";
import YearSelect from "../components/YearSelect";
import CategorySelect from "../components/CategorySelect";
import {
  useAddVehicleMutation,
  useGetVehicleQuery,
  useUpdateVehicleMutation,
  Vehicle,
  VehicleInput,
} from "../graphql/generated/graphql";
import { useToast } from "../context/toast";
import Spinner from "../components/Spinner";
import { ExclamationCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { classNames } from "../utils/formatters";
import EquipmentSelect from "../components/EquipmentSelect";
import ProvinceSelect from "../components/ProvinceSelect";
import MunicipalitySelect from "../components/MunicipalitySelect";
import { useRouter } from "next/router";
import { CheckIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import UserVerified from "./UserVerified";
import ToggleSwitch from "./ToggleSwitch";
import Accordion from "./Accordion";

const CARS_CLOUDINARY_FOLDER_PATH = process.env.NEXT_CLOUDINARY_VEHICLE_FOLDER;
const RADIO_INPUTS = [
  {
    id: "transmission",
    label: "Transmisión",
    fields: TRANSMISSIONS,
  },
  {
    id: "drivetrain",
    label: "Tracción",
    fields: DRIVETRAINS,
  },
  {
    id: "fuel",
    label: "Combustible",
    fields: FUELS,
  },
  {
    id: "condition",
    label: "Condición",
    fields: CONDITIONS,
  },
  {
    id: "rtv",
    label: "Revisión técnica al día",
    fields: YES_NO,
  },
  {
    id: "permit",
    label: "Marchamo al día",
    fields: YES_NO,
  },
  {
    id: "negotiable",
    label: "Negociable",
    fields: YES_NO,
  },
  {
    id: "receivesAnotherVehicle",
    label: "Se recibe vehículo como parte del pago",
    fields: YES_NO,
  },
  {
    id: "priceIncludesTransfer",
    label: "Precio incluye traspaso",
    fields: YES_NO,
  },

  {
    id: "includesFinancing",
    label: "Se ofrece financiamiento",
    fields: YES_NO,
  },
  {
    id: "guarantee",
    label: "Incluye garantía",
    fields: YES_NO,
  },
  {
    id: "platePending",
    label: "Por inscribir",
    fields: YES_NO,
  },
];

const PRICE_INCLUDES_IMPORT_TAXES = {
  id: "priceIncludesImportTaxes",
  label: "Precio incluye impuestos de nacionalización",
  fields: YES_NO,
};

//TODO
type TPayloadProps = {
  data: Record<string, any>;
  imageUrls: string[];
  userId: string;
  purchaseId?: string;
  isContactInfoPublic?: boolean;
};
function isValidYoutubeUrl(url: string) {
  var p =
    /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;

  if (!url || url.match(p)) {
    return true;
  }
  return false;
}
/**Converts a thumb link into a picture link */
function fromThumbToPicture(thumbLink: string) {
  const linkArray = thumbLink.split("/");
  var newPictureLink = "";
  linkArray.forEach((element, index) => {
    if (element != "t_gallery_size") {
      const slash = index + 1 != linkArray.length ? "/" : "";
      newPictureLink = newPictureLink + element + slash;
    }
  });
  return newPictureLink;
}
function thumbsArrayToPicturesArray(thumbsArray: string[]) {
  var picturesArray = [];
  thumbsArray.forEach((element) =>
    picturesArray.push(fromThumbToPicture(element))
  );
  return picturesArray;
}

function getDirtyValues(_id, formData, dirtyFields) {
  const values = { _id };
  Object.keys(dirtyFields).forEach((key) => {
    const isDirty = dirtyFields[key];
    if (isDirty) {
      values[key] = formData[key];
    }
  });

  return values;
}

interface UploadImagesSectionProps {
  errors: Record<string, FieldError>;
  loading: boolean;
  edit: string;
  thumbs: string[];
  setImagesWereReordered: (state: boolean) => void;
  userId: string;
  urlsLength: string;
  imageTags?: string;
  setImageUrls: (urls: string[]) => void;
  setImagesToDeleteFromCloudinary: (imgs: string[]) => void;
  onFilesAdded: (
    files: UppyFile<Record<string, unknown> | Record<string, unknown>>[]
  ) => void;
}

function UploadImagesSection({
  errors,
  loading,
  edit,
  thumbs,
  setImagesWereReordered,
  userId,
  urlsLength,
  imageTags,
  setImageUrls,
  setImagesToDeleteFromCloudinary,
  onFilesAdded,
}: UploadImagesSectionProps) {
  const { register } = useFormContext();
  return (
    <div className={"sm:col-span-6 overflow-visible"}>
      <input
        {...register("urlsLength", {
          required: true,
          validate: (value) => Number(value) >= 1,
        })}
        className="sr-only text-input"
      />
      <FileUploadUppy
        setImagesToDeleteFromCloudinary={setImagesToDeleteFromCloudinary}
        id="add-edit-vehicle"
        tags={`${userId},vehicle,${imageTags}`}
        onChange={setImageUrls}
        invalid={Number(urlsLength) < 1 && !!errors.urlsLength}
        setImagesWereReordered={setImagesWereReordered}
        disabled={loading}
        label={`Fotografías (${MIN_ALLOWED_UPLOADS} min. ${MAX_ALLOWED_UPLOADS} max.)`}
        previewUrls={thumbs}
        min={edit ? 1 : null}
        onFilesAdded={onFilesAdded}
      />
      <div>
        {errors.urlsLength ? (
          <span className="mt-1 text-sm flex gap-x-1 text-red-500 font-medium items-center">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 " />
            Deben haber al menos 4 fotografías.
          </span>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}

interface SecondaryInformationProps {
  inputId: string;
  fuel: string;
  errors: Record<string, FieldError>;
  loading: boolean;
  guarantee: string;
  platePending: string;
  edit: string;
}

function SecondaryInformation({
  inputId,
  fuel,
  errors,
  loading,
  guarantee,
  platePending,
  edit,
}: SecondaryInformationProps) {
  const { register } = useFormContext();
  if (inputId === "fuel" && fuel === FUEL_KEYS.ELECTRIC) {
    return (
      <>
        <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label
              htmlFor="range"
              className="block text-sm font-medium text-gray-700"
            >
              Autonomía
            </label>
            <div className="relative mt-1">
              <input
                {...register("range", {
                  required: true,
                  maxLength: 20,
                })}
                type="number"
                autoComplete="off"
                className={inputClass(errors.range, null, loading)}
                disabled={loading}
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                <label htmlFor="rangeMileageUnit" className="sr-only">
                  Mileage Unit
                </label>
                <select
                  {...register("rangeMileageUnit")}
                  className="h-full rounded-md border-gray-300 rounded-l-none bg-gray-100 py-0 pl-2 pr-7 text-gray-500 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                >
                  {Object.values(MILEAGE_UNIT_KEYS).map((key) => (
                    <option key={key}>{key}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="sm:col-span-3">
            <label
              htmlFor="kwh"
              className="flex gap-x-2 text-sm font-medium text-gray-700"
            >
              KwH
            </label>
            <div className="mt-1">
              <input
                {...register("kwh", {
                  required: true,
                })}
                type="number"
                autoComplete="off"
                className={inputClass(errors.kwh, null, loading)}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </>
    );
  } else if (
    inputId === "fuel" &&
    (fuel === FUEL_KEYS.GASOLINE ||
      fuel === FUEL_KEYS.DIESEL ||
      fuel === FUEL_KEYS.LPG ||
      fuel === FUEL_KEYS.HYBRID)
  ) {
    const required = true;
    return (
      <>
        <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label
              htmlFor="cc"
              className="flex gap-x-2 text-sm font-medium text-gray-700"
            >
              Centímetros Cúbicos
            </label>
            <div className="mt-1">
              <input
                {...register("cc", {
                  required: true,
                })}
                type="number"
                autoComplete="off"
                className={inputClass(errors.cc, null, loading)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="sm:col-span-3">
            <label
              htmlFor="cylinders"
              className="flex gap-x-2 text-sm font-medium text-gray-700"
            >
              Cilindros
            </label>
            <div className="mt-1">
              <input
                {...register("cylinders", {
                  required: true,
                })}
                type="number"
                max={16}
                step={1}
                autoComplete="off"
                className={inputClass(errors.cylinders, null, loading)}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </>
    );
  } else if (inputId === "guarantee" && guarantee === "yes") {
    return (
      <div className="mt-4 sm:col-span-3">
        <label
          htmlFor="guaranteeDescription"
          className="flex gap-x-2 text-sm font-medium text-gray-700"
        >
          Especificar aspectos que cubre la garantía
        </label>
        <div className="mt-1">
          <textarea
            {...register("guaranteeDescription", {
              required: true,
              maxLength: 150,
            })}
            rows={3}
            autoComplete="off"
            className={inputClass(errors.guaranteeDescription, null, loading)}
            disabled={loading}
          />
        </div>
      </div>
    );
  } else if (inputId === "platePending" && platePending === "no") {
    return (
      <div className="mt-4 sm:col-span-3">
        <label
          htmlFor="plate"
          className="flex gap-x-2 text-sm font-medium text-gray-700"
        >
          Número de placa
          <span className="font-normal text-gray-600">{`(Solo será visible el último dígito)`}</span>
        </label>
        <div className="mt-1">
          <input
            {...register("plate", {
              required: true,
              maxLength: 20,
            })}
            type="text"
            autoComplete="off"
            className={inputClass(errors.plate, null, loading)}
            disabled={loading}
          />
        </div>
      </div>
    );
  } else if (inputId === "platePending" && platePending === "yes") {
    return (
      <div className="mt-4 sm:col-span-6">
        <fieldset className="">
          <legend className="contents text-base font-medium text-gray-900">
            Precio incluye costos de nacionalización
          </legend>
          {/* <p className="text-sm text-gray-500">
                      These are delivered via SMS to your mobile phone.
                    </p> */}
          <div className="mt-4 sm:space-x-4 flex  flex-col sm:flex-row sm:items-center">
            {Object.values(PRICE_INCLUDES_IMPORT_TAXES.fields).map((field) => (
              <div key={field.id} className="flex items-center">
                <input
                  {...register(PRICE_INCLUDES_IMPORT_TAXES.id)}
                  id={field.id}
                  type="radio"
                  className={classNames(
                    "focus:ring-orange-500 h-4 w-4 border-gray-300 text-input",
                    loading ? "text-gray-200" : "text-orange-600"
                  )}
                  defaultChecked={!edit ? field.isDefault : null}
                  value={field.id}
                  disabled={loading}
                />
                <label
                  htmlFor={field.id}
                  className="ml-2 block text-sm  text-gray-700"
                >
                  {field.label}
                </label>
              </div>
            ))}
          </div>
        </fieldset>
      </div>
    );
  } else return null;
}

interface RadioInputsProps {
  loading: boolean;
  edit: string;
  errors: Record<string, FieldError>;
  fuel: string;
  guarantee: string;
  platePending: string;
}

function RadioInputsSection({
  loading,
  edit,
  errors,
  fuel,
  guarantee,
  platePending,
}: RadioInputsProps) {
  const { register, control } = useFormContext();
  return (
    <>
      {RADIO_INPUTS.map((input) => (
        <div key={input.id} className="sm:col-span-6">
          <fieldset className="">
            <legend className="contents text-base font-medium text-gray-900">
              {input.label}
            </legend>
            {/* <p className="text-sm text-gray-500">
                      These are delivered via SMS to your mobile phone.
                    </p> */}
            <div className="mt-4 sm:space-x-4 flex  flex-col sm:flex-row sm:items-center">
              {Object.values(input.fields).map((field) => (
                <div key={field.id} className="flex items-center">
                  <input
                    {...register(input.id)}
                    id={field.id}
                    type="radio"
                    className={classNames(
                      "focus:ring-orange-500 h-4 w-4 border-gray-300 text-input",
                      loading ? "text-gray-200" : "text-orange-600"
                    )}
                    defaultChecked={!edit ? field.isDefault : null}
                    value={field.id}
                    disabled={loading}
                  />
                  <label
                    htmlFor={field.id}
                    className="ml-2 block text-sm  text-gray-700"
                  >
                    {field.label}
                  </label>
                </div>
              ))}
            </div>
            <SecondaryInformation
              edit={edit}
              errors={errors}
              fuel={fuel}
              guarantee={guarantee}
              inputId={input.id}
              loading={loading}
              platePending={platePending}
            />
          </fieldset>
        </div>
      ))}

      <div className="sm:col-span-6">
        <Controller
          name="equipment"
          // rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <EquipmentSelect {...field} disabled={loading} />
          )}
        />
      </div>
    </>
  );
}

interface BasicInformationProps {
  loading: boolean;
  errors: Record<string, FieldError>;
  year: string;
  currency: string;
  subscriptionType: string;
}

function BasicInformationSection({
  loading,
  errors,
  year,
  currency,
  subscriptionType,
}: BasicInformationProps) {
  const { register, control } = useFormContext();

  return (
    <>
      <div className="sm:col-span-2 overflow-visible">
        <Controller
          name="brand"
          rules={{ required: true }}
          control={control}
          render={({ field }) => <BrandSelect {...field} disabled={loading} />}
        />
      </div>
      <div className="sm:col-span-2">
        <label
          htmlFor="model"
          className="block text-sm font-medium text-gray-700"
        >
          Modelo
        </label>
        <div className="mt-1">
          <input
            {...register("model", {
              required: true,
              maxLength: 20,
              validate: (value) => {
                return value ? !value.includes(year) : true;
              },
            })}
            type="text"
            autoComplete="off"
            className={inputClass(errors.model, null, loading)}
            disabled={loading}
          />
          {errors.model ? (
            <span className="mt-1 text-sm flex gap-x-1 text-red-500 font-medium items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500 " />
              No incluir año aquí, hacerlo en el campo de Año.
            </span>
          ) : null}
        </div>
      </div>
      <div className="sm:col-span-2">
        <label
          htmlFor="modelVersion"
          className="block text-sm font-medium text-gray-700"
        >
          Versión{" "}
          <span className="font-normal text-gray-600">{`(Opcional)`}</span>
        </label>
        <div className="mt-1">
          <input
            {...register("modelVersion", {
              maxLength: 20,
              validate: (value) => {
                return value ? !value.includes(year) : true;
              },
            })}
            type="text"
            autoComplete="off"
            className={inputClass(errors.modelVersion, null, loading)}
            disabled={loading}
          />
          {errors.modelVersion ? (
            <span className="mt-1 text-sm flex gap-x-1 text-red-500 font-medium items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500 " />
              No incluir año aquí, hacerlo en el campo de Año.
            </span>
          ) : null}
        </div>
      </div>

      <div className="sm:col-span-3 overflow-visible">
        <Controller
          name="category"
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <CategorySelect {...field} disabled={loading} />
          )}
        />
      </div>

      <div className="sm:col-span-3">
        <Controller
          name="year"
          rules={{
            required: true,
            pattern: {
              value: /^(0|[1-9]\d*)(\.\d+)?$/,
              message: "Only numbers allowed",
            },
          }}
          control={control}
          render={({ field }) => <YearSelect {...field} disabled={loading} />}
        />
        {/* <label
                    htmlFor="year"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Año
                  </label>
                  <div className="mt-1">
                    <input
                      {...register("year", {
                        required: true,
                        maxLength: 20,
                      })}
                      type="text"
                      autoComplete="off"
                      className={inputClass(errors.year)}
                    />
                  </div> */}
      </div>

      <div className="sm:col-span-3">
        <label
          htmlFor="mileage"
          className="block text-sm font-medium text-gray-700"
        >
          Kilometraje
        </label>
        <div className="relative mt-1">
          <input
            {...register("mileage", {
              required: true,
              maxLength: 20,
            })}
            type="number"
            autoComplete="off"
            className={inputClass(errors.mileage, null, loading)}
            disabled={loading}
          />
          <div className="absolute inset-y-0 right-0 flex items-center">
            <label htmlFor="mileageUnit" className="sr-only">
              Mileage Unit
            </label>
            <select
              {...register("mileageUnit")}
              className="h-full rounded-md border-gray-300 rounded-l-none bg-gray-100 py-0 pl-2 pr-7 text-gray-500 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            >
              {Object.values(MILEAGE_UNIT_KEYS).map((key) => (
                <option key={key}>{key}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="sm:col-span-3">
        <label
          htmlFor="price"
          className="flex justify-between text-sm font-medium text-gray-700"
        >
          Precio
          {subscriptionType === SUBSCRIPTION_TYPES.pro ? (
            <Controller
              name="hidePrice"
              control={control}
              render={({ field }) => (
                <ToggleSwitch label="Ocultar precio" {...field} />
              )}
            />
          ) : null}
        </label>
        <div className="relative mt-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {/* <span className="text-gray-500 sm:text-sm">$</span> */}

            <span className="text-gray-500 sm:text-sm">
              {CURRENCIES[currency]?.symbol || CURRENCIES.CRC.symbol}
            </span>
          </div>
          <input
            {...register("price", {
              required: true,
              maxLength: 20,
              valueAsNumber: true,
              pattern: {
                value: /^(0|[1-9]\d*)(\.\d+)?$/,
                message: "only numbers",
              },
            })}
            type="number"
            autoComplete="off"
            className={inputClass(
              errors.price,
              "appearance-none pl-6 pr-20 text-input",
              loading
            )}
            disabled={loading}
          />
          <div className="absolute inset-y-0 right-0 flex items-center">
            <label htmlFor="currency" className="sr-only">
              Currency
            </label>
            <select
              {...register("currency")}
              className="h-full rounded-md border-gray-300 rounded-l-none bg-gray-100 py-0 pl-2 pr-7 text-gray-500 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            >
              {Object.values(CURRENCY_KEYS).map((key) => (
                <option key={key}>{key}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="sm:col-span-6">
        <label
          htmlFor="about"
          className="block text-sm font-medium text-gray-700"
        >
          Descripción{" "}
          <span className="font-normal text-gray-600">{`(Opcional)`}</span>
        </label>
        <div className="mt-1">
          <textarea
            {...register("description", {
              maxLength: 300,
            })}
            rows={3}
            className={inputClass(errors.description, null, loading)}
            defaultValue={""}
            disabled={loading}
          />
        </div>
        {/* <p className="mt-2 text-sm text-gray-500">
                  Write a few sentences about yourself.
                </p> */}
      </div>

      <div className="sm:col-span-3 overflow-visible">
        <Controller
          name="province"
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <ProvinceSelect {...field} disabled={loading} />
          )}
        />
      </div>

      <div className="sm:col-span-3 overflow-visible">
        <Controller
          name="municipality"
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <MunicipalitySelect
              {...field}
              disabled={loading}
              provinceField="province"
            />
          )}
        />
      </div>

      <div className="sm:col-span-3">
        <label
          htmlFor="color"
          className="block text-sm font-medium text-gray-700"
        >
          Color Exterior
        </label>
        <div className="mt-1">
          <input
            {...register("color", {
              maxLength: 20,
            })}
            type="text"
            autoComplete="off"
            className={inputClass(errors.color, null, loading)}
            disabled={loading}
          />
        </div>
      </div>

      <div className="sm:col-span-3">
        <label
          htmlFor="interiorColor"
          className="block text-sm font-medium text-gray-700"
        >
          Color Interior
        </label>
        <div className="mt-1">
          <input
            {...register("interiorColor", {
              maxLength: 20,
            })}
            type="text"
            autoComplete="off"
            className={inputClass(errors.interiorColor, null, loading)}
            disabled={loading}
          />
        </div>
      </div>

      <div className="sm:col-span-6">
        <label
          htmlFor="youtubeLink"
          className="block text-sm font-medium text-gray-700"
        >
          <BsYoutube className="inline text-red-600 h-5 w-5 mr-1 mb-0.5" />
          Link de Youtube{" "}
          <span className="font-normal text-gray-600">(Opcional)</span>
        </label>
        <div className="mt-1">
          <input
            {...register("youtubeLink", {
              maxLength: 200,
              validate: (value) => {
                return isValidYoutubeUrl(value);
              },
            })}
            type="text"
            autoComplete="off"
            className={inputClass(errors.youtubeLink, null, loading)}
            disabled={loading}
          />
          {errors.youtubeLink ? (
            <span className="mt-1 text-sm flex gap-x-1 text-red-500 font-medium items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500 " />
              El link ingresado no es un link de Youtube válido
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default function AddEditVehicle({
  _id,
}: {
  _id?: string;
  data?: Vehicle;
}) {
  const router = useRouter();
  const edit = _id;
  const {
    data: { user },
  } = useSession();
  const userId = user?.id;
  const idVerified = user?.idVerified;
  const { toggleToast } = useToast();

  const [imageUrls, setImageUrls] = useState<string[] | null>(null);
  const [imagesWereReordered, setImagesWereReordered] = useState(false);
  const [isContactInfoPublic, setIsContactInfoPublic] = useState(true);
  const [imagesToDeleteFromCloudinary, setImagesToDeleteFromCloudinary] =
    useState<string[]>([]);

  const [accordionStates, setAccordionStates] = useState({
    basicInfo: true,
    images: false,
    extraInfo: false,
  });

  const methods = useForm();
  const { register, handleSubmit, formState, control, setValue, watch, reset } =
    methods;
  const { errors, dirtyFields } = formState;
  const hasErrors = Object.keys(errors).length > 0;
  const urlsLength = watch("urlsLength");
  const year = watch("year");
  const currency = watch("currency");
  const fuel = watch("fuel");
  const guarantee = watch("guarantee");
  const platePending = watch("platePending");
  const municipality = watch("municipality");
  const brand = watch("brand");
  const model = watch("model");
  const modelVersion = watch("modelVersion");

  useEffect(() => {
    //municipality is the last required input
    if (municipality && !accordionStates.images) {
      setAccordionStates({
        ...accordionStates,
        images: !accordionStates.images,
      });
    }
  }, [municipality]);

  useEffect(() => {
    if (!edit) return;
    setAccordionStates({
      basicInfo: true,
      extraInfo: true,
      images: true,
    });
  }, [edit]);

  function openExtraInfoAccordion(
    files: UppyFile<Record<string, unknown> | Record<string, unknown>>[]
  ) {
    if (files.length > 3) {
      setAccordionStates({
        ...accordionStates,
        extraInfo: true,
      });
    }
  }

  function buildPayload(props: TPayloadProps) {
    const { data, imageUrls, userId, isContactInfoPublic } = props;
    const {
      tier,
      brand,
      model,
      modelVersion,
      category,
      mileage,
      range,
      kwh,
      cc,
      cylinders,
      price,
      country,
      province,
      municipality,
      color,
      interiorColor,
      negotiable,
      receivesAnotherVehicle,
      priceIncludesImportTaxes,
      priceIncludesTransfer,
      includesFinancing,
      guarantee,
      platePending,
      rtv,
      permit,
      urlsLength,
      youtubeLink,
      ...restVehicleData
    } = data;

    const payload: VehicleInput = {
      userId,
      ...restVehicleData,
    };

    youtubeLink === "" ? (payload.youtubeLink = youtubeLink) : null;

    const getters = {
      brand: () => brand?.name,
      model: () => model.replace(data.year, ""), // sometimes people will include the year in the model field. We remove it cause it messes up with the text search
      modelVersion: () => modelVersion.replace(data.year, ""), // sometimes people will include the year in the modelVersion field. We remove it cause it messes up with the text search
      category: () => category?.id,
      province: () => province.id,
      municipality: () => municipality,
      youtubeLink: () => youtubeLink,
      // color: () => ({ exterior: color, interior: interiorColor }),
      listingTier: () => tier,
      mileage: () => Math.round(Number(mileage)),
      range: () => Math.round(Number(range)),
      kwh: () => Math.round(Number(kwh)),
      price: () => Math.round(Number(price)),
      cc: () => Math.round(Number(cc)),
      cylinders: () => Math.round(Number(cylinders)),
      negotiable: () => (negotiable === "yes" ? true : false),
      receivesAnotherVehicle: () =>
        receivesAnotherVehicle === "yes" ? true : false,
      priceIncludesImportTaxes: () =>
        priceIncludesImportTaxes === "yes" ? true : false,
      priceIncludesTransfer: () =>
        priceIncludesTransfer === "yes" ? true : false,
      includesFinancing: () => (includesFinancing === "yes" ? true : false),
      guarantee: () => (guarantee === "yes" ? true : false),
      platePending: () => (platePending === "yes" ? true : false),
      rtv: () => (rtv === "yes" ? true : false),
      permit: () => (permit === "yes" ? true : false),
    };

    Object.keys(data).forEach((key) => {
      if (data[key] && getters[key]) {
        payload[key] = getters[key]();
      }
    });

    payload.color = {};

    if (color) {
      payload.color.exterior = color;
    }

    if (interiorColor) {
      payload.color.interior = interiorColor;
    }

    if (imagesWereReordered) {
      payload.pictures = thumbsArrayToPicturesArray(imageUrls);
    }

    payload.isContactInfoPublic = isContactInfoPublic;
    return {
      variables: {
        vehicle: payload,
      },
    };
  }
  const {
    data: vehicleData,
    loading: loadingVehicleData,
    refetch,
  } = useGetVehicleQuery({
    skip: !_id,
    variables: {
      _id,
    },
    onCompleted: ({ getVehicle }) => {
      const {
        brand,
        category,
        pictures,
        color,
        location,
        negotiable,
        receivesAnotherVehicle,
        priceIncludesImportTaxes,
        priceIncludesTransfer,
        includesFinancing,
        guarantee,
        platePending,
        rtv,
        permit,
        youtubeLink,
        // ignored start
        __typename,
        user,
        expires,
        createdDate,
        thumbs,
        isContactInfoPublic,
        // ignored end
        ...restVehicle
      } = getVehicle;
      reset({
        ...restVehicle,
        youtubeLink,
        brand: { name: brand.toUpperCase() },
        category: CATEGORIES[category],
        color: color.exterior,
        interiorColor: color.interior,
        urlsLength: pictures.join().length,
        province: PROVINCES[location.province],
        municipality: PROVINCES[location.province].municipalities.find(
          (m) => m.toLowerCase() === location.municipality
        ),
        negotiable: negotiable ? "yes" : "no",
        receivesAnotherVehicle: receivesAnotherVehicle ? "yes" : "no",
        priceIncludesImportTaxes: priceIncludesImportTaxes ? "yes" : "no",
        priceIncludesTransfer: priceIncludesTransfer ? "yes" : "no",
        includesFinancing: includesFinancing ? "yes" : "no",
        guarantee: guarantee ? "yes" : "no",
        platePending: platePending ? "yes" : "no",
        rtv: rtv ? "yes" : "no",
        permit: permit ? "yes" : "no",
      });

      setIsContactInfoPublic(isContactInfoPublic);

      setImageUrls(pictures);
    },
  });

  const { getVehicle } = vehicleData || {};
  const { thumbs, pictures } = getVehicle || {};

  const [publishVehicle, { loading: publishingVehicle }] =
    useAddVehicleMutation({
      onCompleted({ addVehicle: vehicle }) {
        const pathname = `/destacar/${vehicle._id}`;
        router.push(
          {
            pathname,
            // query: { data: JSON.stringify(vehicle) },
          },
          pathname
        );
      },
      onError() {
        toggleToast({
          open: true,
          title: "Error",
          description: "Hubo un error al publicar el vehículo",
          type: "error",
        });
      },
    });

  const [updateVehicle, { loading: updatingVehicle }] =
    useUpdateVehicleMutation({
      onCompleted({ updateVehicle: vehicle }) {
        refetch();
        router.push(`/autosusados/${vehicle.slug}`);
      },
      onError() {
        toggleToast({
          open: true,
          title: "Error",
          description: "Hubo un error al editar el vehículo",
          type: "error",
        });
      },
    });

  async function removeCloudinaryImage(
    image_url: string,
    CLOUDINARY_FOLDER_PATH: string
  ) {
    try {
      const response = await fetch("/api/cloudinary/destroy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image_url: image_url,
          CLOUDINARY_FOLDER_PATH: CLOUDINARY_FOLDER_PATH,
        }),
      });
      if (!response.ok) {
        throw Error("Error while fetching");
      }
      return true;
    } catch (err) {
      toggleToast({
        title: "Error",
        description: "Error while deleting image from db",
        open: true,
        type: "error",
      });
      console.error(err.response?.data ?? "There was an error");
      return false;
    }
  }

  async function removeImagesFromCloudinary() {
    if (imagesToDeleteFromCloudinary.length > 0) {
      //
      const promises: Promise<boolean>[] = [];

      imagesToDeleteFromCloudinary.forEach((value) => {
        promises.push(
          removeCloudinaryImage(value, CARS_CLOUDINARY_FOLDER_PATH)
        );
      });

      Promise.all(promises).then((values) => {
        console.log(values);
      });
    }
  }

  useEffect(() => {
    if (imageUrls) {
      setValue("urlsLength", imageUrls.join().length, { shouldDirty: true });
    }
  }, [imageUrls]);

  const setContactInfoPublic = () => {
    setIsContactInfoPublic(true);
  };

  const setContactInfoPrivate = () => {
    setIsContactInfoPublic(false);
  };

  //submit func
  const onSubmit = async (formData) => {
    const updatedData = getDirtyValues(_id, formData, dirtyFields);
    const data = edit ? updatedData : formData;

    const mutationFunction = edit ? updateVehicle : publishVehicle;

    //delete from  cloudinary
    removeImagesFromCloudinary();

    mutationFunction(
      buildPayload({ data, imageUrls, userId, isContactInfoPublic })
    );
  };

  const loading = loadingVehicleData || publishingVehicle || updatingVehicle;

  return (
    <div className="max-w-7xl mx-auto px-4 pt-6 sm:pt-10">
      <FormProvider {...methods}>
        <form
          className="space-y-8 divide-y divide-gray-200"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="space-y-8 divide-y divide-gray-200">
            <div>
              <ComboInfoPanel />
              <div className="mt-2">
                <UserVerified idVerified={idVerified} />
              </div>

              <div className="mt-6">
                <h1 className="text-lg leading-6 font-medium text-gray-900">
                  {edit ? "Editar" : "Vender"} vehículo
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Esta información se mostrará publicamente.
                </p>
              </div>

              <div className="mt-6 overflow-visible">
                <Accordion
                  title="1. Información básica"
                  Component={
                    <BasicInformationSection
                      currency={currency}
                      errors={errors as Record<string, FieldError>}
                      loading={loading}
                      subscriptionType={user.subscriptionType}
                      year={year}
                    />
                  }
                  accordionOpen={accordionStates.basicInfo}
                  setAccordionOpen={() =>
                    setAccordionStates({
                      ...accordionStates,
                      basicInfo: !accordionStates.basicInfo,
                    })
                  }
                />

                <Accordion
                  title="2. Fotografías"
                  Component={
                    <UploadImagesSection
                      edit={edit}
                      errors={errors as Record<string, FieldError>}
                      loading={loading}
                      setImageUrls={setImageUrls}
                      setImagesToDeleteFromCloudinary={
                        setImagesToDeleteFromCloudinary
                      }
                      setImagesWereReordered={setImagesWereReordered}
                      thumbs={thumbs}
                      urlsLength={urlsLength}
                      userId={userId}
                      onFilesAdded={openExtraInfoAccordion}
                      imageTags={`${brand?.name},${model},${modelVersion},${year}`}
                    />
                  }
                  accordionOpen={accordionStates.images}
                  setAccordionOpen={() =>
                    setAccordionStates({
                      ...accordionStates,
                      images: !accordionStates.images,
                    })
                  }
                />

                <Accordion
                  title="3. Información adicional"
                  Component={
                    <RadioInputsSection
                      edit={edit}
                      errors={errors as Record<string, FieldError>}
                      fuel={fuel}
                      guarantee={guarantee}
                      loading={loading}
                      platePending={platePending}
                    />
                  }
                  accordionOpen={accordionStates.extraInfo}
                  setAccordionOpen={() =>
                    setAccordionStates({
                      ...accordionStates,
                      extraInfo: !accordionStates.extraInfo,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="pt-5">
            {/* Adding error message when there are form errors */}
            {hasErrors ? (
              <div className="rounded-md bg-red-50 p-4 mb-5">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircleIcon
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Algunos campos requeridos no han sido ingresados o
                      presentan errores.
                    </h3>
                  </div>
                </div>
              </div>
            ) : null}
            <div>
              <label
                htmlFor="plate"
                className="contents text-base font-medium text-gray-900"
              >
                Información de contacto
              </label>
              <div className="my-2 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 grid-cols-1 sm:gap-4 lg:max-w-7xl lg:mx-auto xl:max-w-none xl:mx-0">
                <div
                  onClick={setContactInfoPrivate}
                  className={classNames(
                    "rounded-lg shadow-sm  cursor-pointer hover:bg-orange-50 hover:border-orange-500",
                    !isContactInfoPublic
                      ? "border-2 border-orange-500"
                      : "border border-gray-200"
                  )}
                >
                  <div className="relative p-6">
                    {!isContactInfoPublic ? (
                      <CheckIcon className="h-8 w-8 text-orange-500 absolute right-4 top-4" />
                    ) : null}
                    <h2 className="text-lg leading-6 font-medium text-gray-900">
                      Privada
                    </h2>

                    <EyeSlashIcon className="h-12 w-12 text-gray-500" />

                    <p className="mt-3 text-md text-gray-500">
                      Tu vehículo será visible públicamente pero solo usuarios
                      registrados en moviautos.com podrán contactarte.
                    </p>
                  </div>
                </div>

                <div
                  onClick={setContactInfoPublic}
                  className={classNames(
                    "rounded-lg shadow-sm  cursor-pointer hover:bg-orange-50 hover:border-orange-500",
                    isContactInfoPublic
                      ? "border-2 border-orange-500"
                      : "border border-gray-200"
                  )}
                >
                  <div className="relative p-6">
                    {isContactInfoPublic ? (
                      <CheckIcon className="h-8 w-8 text-orange-500 absolute right-4 top-4" />
                    ) : null}
                    <h2 className="text-lg leading-6 font-medium text-gray-900">
                      Pública
                    </h2>
                    <EyeIcon className="h-12 w-12 text-gray-500" />
                    <p className="mt-3 text-md text-gray-500">
                      Cualquier persona podrá contactarte aún cuando no estén
                      registrados en moviautos.com.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              {/* <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Cancelar
              </button> */}
              <button
                type="submit"
                className="w-24 ml-3 inline-flex gap-x-1 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                disabled={loading}
                onClick={() => {
                  const hasErrors = Object.keys(errors).length > 0;

                  if (!hasErrors) return;

                  //there's some error; open all accordions to show them
                  setAccordionStates({
                    basicInfo: true,
                    extraInfo: true,
                    images: true,
                  });
                }}
              >
                {loading ? <Spinner className="h-5 w-5" /> : null}
                {edit ? "Guardar" : "Publicar"}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
